import { Hosts, host } from "@/config/host-objects";
import {
  IHostFeatureConfig,
  INavbarItemConfig,
  NavSection,
  getHostFeatureConfigByKey,
  hostFeatureConfigMap,
} from "@/config/host-feature-config";

export function getActiveHostKey(): Hosts | undefined {
  const keys = Object.keys(Hosts) as (keyof typeof Hosts)[];
  const found = keys.find((key) => Hosts[key] === host);
  return found ? (Hosts[found] as Hosts) : undefined;
}

export function getHostFeatureConfig(): IHostFeatureConfig {
  const key = getActiveHostKey();
  if (!key) return {};
  return getHostFeatureConfigByKey(key);
}

export function isActiveHostConfigured(): boolean {
  const key = getActiveHostKey();
  return !!key && !!hostFeatureConfigMap[key];
}

export function getNavSectionConfig(section: NavSection): INavbarItemConfig {
  return getHostFeatureConfig().navbar?.[section] || {};
}

export function isNavSectionHidden(section: NavSection): boolean {
  return !!getNavSectionConfig(section).hidden;
}

export function isNavChildHidden(
  section: NavSection,
  routeName: string
): boolean {
  const cfg = getNavSectionConfig(section);
  return !!cfg.hiddenChildren?.includes(routeName);
}

export function getNavSingleChild(section: NavSection): string | undefined {
  return getNavSectionConfig(section).singleChild;
}

const singleChildLabelKeys: Record<string, string> = {
  MenuList: "nav.MenuPage",
  RouteInfoV2Page: "nav.RouteInfoV2Page",
};

export function getNavSingleChildLabel(section: NavSection): string {
  const routeName = getNavSingleChild(section);
  if (!routeName) return "";
  return singleChildLabelKeys[routeName] || "";
}

export function isFiloxeniaAppHidden(app: string): boolean {
  return !!getHostFeatureConfig().configuration?.hiddenApps?.includes(app);
}

export function isGuestRuleHidden(rule: string): boolean {
  return !!getHostFeatureConfig().users?.hiddenRules?.includes(rule);
}

export function isRoomMediaHidden(media: string): boolean {
  return !!getHostFeatureConfig().hotel?.hiddenRoomMedia?.includes(media);
}

export function isRoomLevelRenamedToCategory(): boolean {
  return !!getHostFeatureConfig().hotel?.roomLevelAsCategory;
}

export function isMusterRollRenamedToPositions(): boolean {
  return !!getHostFeatureConfig().staff?.musterRollAsPositions;
}

export function isMenuBarsManagementHidden(): boolean {
  return !!getHostFeatureConfig().menu?.hideBarsManagement;
}

export function isMenuHistoryHidden(): boolean {
  return !!getHostFeatureConfig().menu?.hideHistory;
}

export function getHiddenRoomServiceTypes(): string[] {
  return getHostFeatureConfig().roomService?.hiddenServiceTypes || [];
}

export function isRoomServiceTypeHidden(type: string): boolean {
  return getHiddenRoomServiceTypes().includes(type);
}

export function isFaqHidden(): boolean {
  return !!getHostFeatureConfig().faq?.hidden;
}

export function isBigNavbarHidden(): boolean {
  return !!getHostFeatureConfig().header?.hideBigNavbar;
}

export function isSettingsOptionHidden(option: string): boolean {
  return !!getHostFeatureConfig().settings?.hiddenOptions?.includes(option);
}

export function getActiveGuideProfile(): string {
  return getHostFeatureConfig().guideProfile || "";
}
